import {useState} from "react";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {ArrowLeft, Trash2} from "lucide-react";
import {v4 as UUID} from "uuid";
import Encounter from "@/encounter/model/Encounter.ts";
import {EncounterFormInput, EncounterFormOutput, encounterSchema} from "./EncounterFormSchema.ts";
import {Form, FormField, FormItem, FormMessage} from "@/components/ui/form.tsx";
import {Button} from "@/components/ui/button.tsx";
import TextInputField from "@/components/forms/TextInputField.tsx";
import SaveButton, {SaveState} from "@/components/buttons/SaveButton.tsx";
import ImageUploadField from "@/images/ImageUploadField.tsx";
import {useTemporaryImageFormField} from "@/images/useTemporaryImageFormField.ts";
import {useCampaign} from "@/campaigns/CampaignContextHooks.ts";
import {defaultViewPort} from "@/encounter/model/ViewPort.ts";
import {notifyError} from "@/api/ErrorNotification.ts";
import EncounterDungeonForm from "./EncounterDungeonForm.tsx";

export default function EncounterForm({encounter, onSubmit, onCancel}: {
    encounter?: Encounter;
    onSubmit: (encounter: Encounter) => Promise<void>;
    onCancel: () => void;
}) {
    const [saveState, setSaveState] = useState<SaveState>("idle");
    const [error, setError] = useState("");
    const {campaign} = useCampaign();
    const form = useForm<EncounterFormInput, unknown, EncounterFormOutput>({
        resolver: zodResolver(encounterSchema),
        mode: "onBlur",
        defaultValues: encounter ?? {
            id: UUID(), name: "", campaignId: campaign.id, round: 0,
            viewPort: {...defaultViewPort}, coverImage: null,
        },
    });
    const coverImage = useTemporaryImageFormField({form, name: "coverImage", onUploadStart: () => setError("")});

    const onValid = async (data: EncounterFormOutput) => {
        if (coverImage.uploading) return;
        setSaveState("saving");
        setError("");
        try {
            await onSubmit({...data, coverImageId: data.coverImage?.id ?? null});
            coverImage.commit();
            form.reset(data);
            setSaveState("success");
        } catch (reason) {
            setSaveState("error");
            notifyError(reason, "Der Encounter konnte nicht gespeichert werden.");
        }
    };

    return <main className="min-h-0 grow overflow-auto p-3">
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onValid, () => setError("Bitte überprüfe die markierten Eingaben und wähle einen Dungeon."))}
                  className="mx-auto max-w-5xl space-y-5">
                <Button type="button" variant="ghost" size="sm" className="-ml-2 text-muted-foreground"
                        onClick={onCancel}>
                    <ArrowLeft/> {encounter ? "Zurück zum Encounter" : "Alle Encounter"}
                </Button>
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <h1 className="text-3xl font-semibold tracking-tight">{encounter ? `${encounter.name} bearbeiten` : "Neuer Encounter"}</h1>
                    <SaveButton saveState={saveState} disabled={coverImage.uploading || saveState === "saving" ||
                        Boolean(encounter && !form.formState.isDirty)}/>
                </div>
                {error && <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
                <section className="grid gap-6 rounded-xl border border-border/80 bg-card/90 p-5 shadow-lg shadow-black/10 md:grid-cols-[minmax(0,1fr)_minmax(14rem,20rem)]">
                    <div>
                        <TextInputField name="name" title="Name" placeholder="Encountername"/>
                        <p className="mt-2 text-sm text-muted-foreground">Kampagne: {campaign.title}</p>
                        <p className="mt-3 text-sm text-muted-foreground">Das Titelbild erscheint in der Encounter-Übersicht. Ohne eigenes Bild wird das Dungeon-Bild verwendet.</p>
                    </div>
                    <FormField control={form.control} name="coverImage" render={() => <FormItem>
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium">Titelbild (optional)</span>
                            {coverImage.image && <Button type="button" variant="ghost" size="sm"
                                onClick={coverImage.clearImage} aria-label="Titelbild entfernen"><Trash2/>Entfernen</Button>}
                        </div>
                        <div className="aspect-video overflow-hidden rounded-lg border bg-surface">
                            <ImageUploadField image={coverImage.image} uploading={coverImage.uploading}
                                onFileSelected={coverImage.onFileSelected} alt="Encounter-Titelbild" className="size-full"/>
                        </div>
                        <p className="text-xs text-muted-foreground">Empfohlenes Seitenverhältnis: 16:9</p>
                        {coverImage.image && coverImage.image.height > 0 &&
                            Math.abs(coverImage.image.width / coverImage.image.height - 16 / 9) > 0.1 &&
                            <p className="text-sm text-amber-400">Dieses Bild weicht von 16:9 ab und wird auf der Karte möglicherweise beschnitten.</p>}
                        <FormMessage/>
                    </FormItem>}/>
                </section>
                <EncounterDungeonForm fixed={Boolean(encounter)}/>
            </form>
        </Form>
    </main>;
}
